/**
 * /well-testing/packages/new and /well-testing/packages/:id — one package
 * (fieldiq-api#1245, WT-2).
 *
 * The page lists the package's units with each unit's well-testing SPEC: the
 * component kind, the MAWP and PSV set pressure AS ENTERED with their units
 * (the API also keeps a kPa copy, for totals — not shown), sour service, and
 * the PSV recertification due day. A unit's spec is edited in a dialog
 * (PUT /equipment-specs/:equipmentId, an upsert).
 *
 * A unit can be in one package at a time (409 unit_in_other_package). A
 * package on an open job cannot be deleted (409 package_on_open_job), and the
 * refusal names the job. Writes are WT_DISPATCH.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Boxes, Package, Pencil, Plus, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useAddWtPackageUnit,
  useCreateWtPackage,
  useDeleteWtPackage,
  useRemoveWtPackageUnit,
  useSaveWtEquipmentSpec,
  useUpdateWtPackage,
  useWtEquipmentOptions,
  useWtEquipmentSpecs,
  useWtMeta,
  useWtPackage,
} from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blankToNull, canDispatchWt, catalogLabel, wtDay, wtQuantity, wtRefusal } from '../../lib/well-testing';
import type { WtCatalog, WtEquipmentSpec, WtPackageDetail, WtPackageUnit } from '../../types/well-testing';
import { useBackToList } from '../downhole/recordPage';
import {
  isNotFound,
  WT_BAND_SECONDARY,
  WT_DATE_INPUT,
  WT_EYEBROW,
  WT_INPUT,
  WT_PRIMARY,
  WT_SECONDARY,
  WT_SOUR_SKIN,
  WtBadge,
  WtDialog,
  WtEmpty,
  WtField,
  WtFormError,
  WtLoading,
  WtPanel,
  WtRegister,
  WtSpec,
  type WtColumn,
} from './WtBits';

const SETTLED = { isLoading: false };

function PackageForm({
  pkg,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  pkg: WtPackageDetail | null;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: { name: string; description: string | null; notes: string | null }) => void;
}) {
  const [form, setForm] = useState({ name: pkg?.name ?? '', description: pkg?.description ?? '', notes: pkg?.notes ?? '' });
  const [localError, setLocalError] = useState<string | null>(null);
  return (
    <form
      className="space-y-3 px-4 py-4"
      data-testid="wt-package-form"
      onSubmit={(e) => {
        e.preventDefault();
        setLocalError(null);
        if (!form.name.trim()) return setLocalError('Name the package.');
        onSubmit({ name: form.name.trim(), description: blankToNull(form.description), notes: blankToNull(form.notes) });
      }}
    >
      <WtFormError message={localError ?? error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <WtField label="Name">
          <input className={WT_INPUT} aria-label="Package name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </WtField>
        <WtField label="Description">
          <input className={WT_INPUT} aria-label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </WtField>
      </div>
      <WtField label="Notes">
        <textarea className={`${WT_INPUT} h-16 py-2`} aria-label="Package notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WT_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

/** The spec dialog: kind, MAWP (as entered, with unit), PSV set, sour, CRN / NB, recert due. */
function SpecDialog({
  unit,
  spec,
  catalog,
  pressureUnit,
  onClose,
}: {
  unit: WtPackageUnit | null;
  spec: WtEquipmentSpec | null;
  catalog: WtCatalog | null;
  pressureUnit: string | null;
  onClose: () => void;
}) {
  const save = useSaveWtEquipmentSpec();
  const units = catalog?.quantityUnits?.pressure?.accepted ?? (pressureUnit ? [pressureUnit] : []);
  const start = () => ({
    component_kind: spec?.componentKind ?? unit?.componentKind ?? '',
    mawp: spec?.mawp?.entered?.value ?? '',
    mawp_unit: spec?.mawp?.entered?.unit ?? pressureUnit ?? units[0] ?? '',
    psv_set: spec?.psvSet?.entered?.value ?? '',
    psv_set_unit: spec?.psvSet?.entered?.unit ?? pressureUnit ?? units[0] ?? '',
    size_text: spec?.sizeText ?? '',
    sour_service: spec?.sourService ?? false,
    crn: spec?.crn ?? '',
    nb_number: spec?.nbNumber ?? '',
    psv_recert_due: spec?.psvRecertDue ?? '',
    notes: spec?.notes ?? '',
  });
  const [form, setForm] = useState(start);
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    if (unit) {
      setForm(start());
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit?.id]);
  const num = /^\d+(\.\d+)?$/;
  const submit = () => {
    setError(null);
    if (!unit) return;
    if (!form.component_kind) return setError('Choose what kind of unit this is.');
    if (form.mawp.trim() && !num.test(form.mawp.trim())) return setError('The MAWP must be a number, or blank.');
    if (form.psv_set.trim() && !num.test(form.psv_set.trim())) return setError('The PSV set pressure must be a number, or blank.');
    save.mutate(
      {
        equipmentId: unit.equipmentId,
        body: {
          component_kind: form.component_kind,
          mawp: blankToNull(form.mawp),
          mawp_unit: form.mawp.trim() ? form.mawp_unit : null,
          psv_set: blankToNull(form.psv_set),
          psv_set_unit: form.psv_set.trim() ? form.psv_set_unit : null,
          size_text: blankToNull(form.size_text),
          sour_service: form.sour_service,
          crn: blankToNull(form.crn),
          nb_number: blankToNull(form.nb_number),
          psv_recert_due: blankToNull(form.psv_recert_due),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Spec saved');
          onClose();
        },
        onError: (err) => setError(wtRefusal(err, 'The spec could not be saved.')),
      },
    );
  };
  const pressure = (key: 'mawp' | 'psv_set', label: string) => (
    <WtField label={label}>
      <div className="flex gap-2">
        <input className={WT_INPUT} inputMode="decimal" aria-label={label} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
        <select
          className={`${WT_INPUT} w-24 shrink-0`}
          aria-label={`${label} unit`}
          value={form[`${key}_unit` as 'mawp_unit']}
          onChange={(e) => setForm({ ...form, [`${key}_unit`]: e.target.value })}
        >
          {units.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </div>
    </WtField>
  );
  return (
    <WtDialog
      open={!!unit}
      wide
      title={`Spec · ${unit?.equipmentName ?? 'unit'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WT_SECONDARY} onClick={onClose} disabled={save.isPending}>
            Cancel
          </button>
          <button type="button" className={WT_PRIMARY} onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save spec'}
          </button>
        </>
      }
    >
      <WtFormError message={error} />
      <div className="grid gap-3 sm:grid-cols-2" data-testid="wt-spec-form">
        <WtField label="Kind">
          <select className={WT_INPUT} aria-label="Component kind" value={form.component_kind} onChange={(e) => setForm({ ...form, component_kind: e.target.value })}>
            <option value="">Choose…</option>
            {(catalog?.componentKinds ?? []).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Size">
          <input className={WT_INPUT} aria-label="Size" value={form.size_text} onChange={(e) => setForm({ ...form, size_text: e.target.value })} />
        </WtField>
        {pressure('mawp', 'MAWP')}
        {pressure('psv_set', 'PSV set')}
        <WtField label="PSV recert due">
          <input type="date" className={WT_DATE_INPUT} aria-label="PSV recert due" value={form.psv_recert_due} onChange={(e) => setForm({ ...form, psv_recert_due: e.target.value })} />
        </WtField>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={form.sour_service} onChange={(e) => setForm({ ...form, sour_service: e.target.checked })} />
          Rated for sour service
        </label>
        <WtField label="CRN">
          <input className={WT_INPUT} aria-label="CRN" value={form.crn} onChange={(e) => setForm({ ...form, crn: e.target.value })} />
        </WtField>
        <WtField label="NB number">
          <input className={WT_INPUT} aria-label="NB number" value={form.nb_number} onChange={(e) => setForm({ ...form, nb_number: e.target.value })} />
        </WtField>
      </div>
      <div className="mt-3">
        <WtField label="Notes">
          <input className={WT_INPUT} aria-label="Spec notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </WtField>
      </div>
    </WtDialog>
  );
}

export default function WtPackageDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const back = useBackToList('/well-testing/packages');
  const { user } = useAuth();
  const canWrite = canDispatchWt(user?.role);
  const query = useWtPackage(id);
  const metaQuery = useWtMeta();
  const catalog = metaQuery.data?.data ?? null;
  const pressureUnit = metaQuery.data?.meta.units?.pressure ?? null;
  const specs = useWtEquipmentSpecs(!isNew);
  const equipment = useWtEquipmentOptions(canWrite && !isNew);
  const create = useCreateWtPackage();
  const update = useUpdateWtPackage();
  const del = useDeleteWtPackage();
  const addUnit = useAddWtPackageUnit();
  const removeUnit = useRemoveWtPackageUnit();
  const [editing, setEditing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [picking, setPicking] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [specFor, setSpecFor] = useState<WtPackageUnit | null>(null);
  const [removing, setRemoving] = useState<WtPackageUnit | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const pkg = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const specOf = useMemo(() => {
    const m = new Map<string, WtEquipmentSpec>();
    for (const s of specs.data?.data ?? []) m.set(s.equipmentId, s);
    return m;
  }, [specs.data]);
  const inPackage = useMemo(() => new Set((pkg?.units ?? []).map((u) => u.equipmentId)), [pkg]);
  const pickable = (equipment.data ?? []).filter((e) => !inPackage.has(e.id));

  const columns: WtColumn<WtPackageUnit>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (u) => (
        <>
          <p className="font-extrabold text-white">{u.equipmentName || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{u.unitNumber || u.serialNumber || '—'}</p>
        </>
      ),
    },
    { key: 'kind', header: 'Kind', render: (u) => u.componentKindLabel || catalogLabel(catalog?.componentKinds, specOf.get(u.equipmentId)?.componentKind ?? u.componentKind) },
    { key: 'mawp', header: 'MAWP', render: (u) => <span className="whitespace-nowrap font-mono text-sm">{wtQuantity(specOf.get(u.equipmentId)?.mawp)}</span> },
    { key: 'psv', header: 'PSV set', render: (u) => <span className="whitespace-nowrap font-mono text-sm">{wtQuantity(specOf.get(u.equipmentId)?.psvSet)}</span> },
    { key: 'recert', header: 'PSV recert due', render: (u) => <span className="whitespace-nowrap">{wtDay(specOf.get(u.equipmentId)?.psvRecertDue)}</span> },
    {
      key: 'sour',
      header: 'Sour',
      render: (u) => {
        const s = specOf.get(u.equipmentId);
        return !s ? '—' : s.sourService ? <WtBadge skin={WT_SOUR_SKIN} value="sour" /> : 'No';
      },
    },
    { key: 'available', header: 'Available', render: (u) => (u.available === false ? 'Retired / archived' : u.available ? 'Yes' : '—') },
  ];
  if (canWrite)
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (u) => (
        <span className="inline-flex gap-2 whitespace-nowrap">
          <button type="button" className={WT_SECONDARY} aria-label={`Edit spec for ${u.equipmentName ?? 'unit'}`} onClick={() => setSpecFor(u)}>
            <Pencil className="size-4" aria-hidden="true" />
            Spec
          </button>
          <button type="button" className={WT_SECONDARY} aria-label={`Remove ${u.equipmentName ?? 'unit'}`} onClick={() => { setRemoveError(null); setRemoving(u); }}>
            <Trash2 className="size-4" aria-hidden="true" />
            Remove
          </button>
        </span>
      ),
    });

  if (isNew)
    return (
      <div data-testid="wt-new-package-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow={WT_EYEBROW} title="New package" description="Name it now; add its units on the next page." onBack={back} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px] space-y-4">
            <WtPanel eyebrow="Package register" title="Package">
              {!canWrite ? (
                <WtEmpty>Only an admin, office staff, a supervisor or a dispatcher can make a package.</WtEmpty>
              ) : (
                <PackageForm
                  pkg={null}
                  pending={create.isPending}
                  error={formError}
                  submitLabel="Create package"
                  onCancel={back}
                  onSubmit={(body) => {
                    setFormError(null);
                    create.mutate(body, {
                      onSuccess: (res) => {
                        toast.success('Package created');
                        navigate(res.data?.id ? `/well-testing/packages/${res.data.id}` : '/well-testing/packages', { replace: true });
                      },
                      onError: (err) => setFormError(wtRefusal(err, 'The package could not be created.')),
                    });
                  }}
                />
              )}
            </WtPanel>
          </div>
        </section>
      </div>
    );

  return (
    <div data-testid="wt-package-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title={pkg?.name || (query.isLoading ? 'Package' : '—')}
        description={pkg?.description ?? undefined}
        onBack={back}
        backLabel="Back"
        actions={
          pkg && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={WT_BAND_SECONDARY} disabled={editing} onClick={() => { setFormError(null); setEditing(true); }}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={WT_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Boxes className="size-4" aria-hidden="true" />, label: 'Units', tone: 'neutral', loading: query.isLoading, value: pkg ? displayValue(pkg.units.length) : '—' },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'On job', tone: 'neutral', loading: query.isLoading, value: pkg?.openJobNumber || '—' },
          { icon: <Package className="size-4" aria-hidden="true" />, label: 'Sour rated', tone: 'neutral', loading: query.isLoading || specs.isLoading, value: pkg && !specs.isLoading && !queryLoadFailure(specs) ? displayValue(pkg.units.filter((u) => specOf.get(u.equipmentId)?.sourService).length) : '—' },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WtLoading label="Loading package" />
          ) : notFound || (!failure && !pkg) ? (
            <WtPanel eyebrow="Package" title="Package">
              <WtEmpty>This package was not found. It may have been deleted.</WtEmpty>
            </WtPanel>
          ) : failure || !pkg ? (
            <WtPanel eyebrow="Package" title="Package">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this package" />
            </WtPanel>
          ) : (
            <>
              <WtPanel eyebrow="Package" title="Details" testId="wt-package-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WtSpec label="Name" value={pkg.name || '—'} />
                  <WtSpec label="Description" value={pkg.description || '—'} />
                  <WtSpec label="On open job" value={pkg.openJobNumber || '—'} mono />
                  <WtSpec label="Notes" value={pkg.notes || '—'} />
                </dl>
              </WtPanel>
              {editing && canWrite && (
                <WtPanel eyebrow="Edit" title="Edit package">
                  <PackageForm
                    pkg={pkg}
                    pending={update.isPending}
                    error={formError}
                    submitLabel="Save package"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) =>
                      update.mutate(
                        { id: pkg.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Package saved');
                            setEditing(false);
                          },
                          onError: (err) => setFormError(wtRefusal(err, 'The package could not be saved.')),
                        },
                      )
                    }
                  />
                </WtPanel>
              )}
              <WtPanel
                eyebrow="Units"
                title="Units and specs"
                testId="wt-package-units"
                actions={
                  canWrite ? (
                    <form
                      className="flex items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setAddError(null);
                        if (!picking) return setAddError('Choose a unit to add.');
                        addUnit.mutate(
                          { packageId: pkg.id, equipmentId: picking },
                          {
                            onSuccess: () => {
                              toast.success('Unit added to the package');
                              setPicking('');
                            },
                            onError: (err) => setAddError(wtRefusal(err, 'The unit could not be added.')),
                          },
                        );
                      }}
                    >
                      <select className={`${WT_INPUT} w-56`} aria-label="Unit to add" value={picking} onChange={(e) => setPicking(e.target.value)}>
                        <option value="">{equipment.isLoading ? 'Loading…' : 'Add a unit…'}</option>
                        {pickable.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.name}
                            {e.unitNumber ? ` · ${e.unitNumber}` : ''}
                          </option>
                        ))}
                      </select>
                      <button type="submit" className={WT_PRIMARY} disabled={addUnit.isPending}>
                        <Plus className="size-4" aria-hidden="true" />
                        Add
                      </button>
                    </form>
                  ) : undefined
                }
              >
                {addError && (
                  <div className="px-4 pt-4">
                    <WtFormError message={addError} />
                  </div>
                )}
                {queryLoadFailure(specs) && (
                  <div className="px-4 pt-4">
                    <LoadFailureNotice error={queryFailureError(specs)} kind={queryLoadFailure(specs) ?? 'unreachable'} subject="the unit specs" />
                  </div>
                )}
                <WtRegister
                  query={SETTLED}
                  rows={pkg.units}
                  columns={columns}
                  rail={(u) => (u.available === false ? 'border-l-fi-red' : 'border-l-transparent')}
                  subject="the package's units"
                  testId="wt-package-unit"
                  minWidth="min-w-[900px]"
                  empty={<WtEmpty>This package has no units yet.</WtEmpty>}
                  card={(u) => {
                    const s = specOf.get(u.equipmentId);
                    return (
                      <>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-extrabold text-white">{u.equipmentName || '—'}</p>
                            <p className="mt-0.5 text-xs text-fieldiq-muted">{u.componentKindLabel || '—'}</p>
                          </div>
                          {s?.sourService && <WtBadge skin={WT_SOUR_SKIN} value="sour" />}
                        </div>
                        <p className="mt-2 text-xs text-fieldiq-text">
                          MAWP {wtQuantity(s?.mawp)} · PSV {wtQuantity(s?.psvSet)} · Recert {wtDay(s?.psvRecertDue)}
                        </p>
                        {canWrite && (
                          <button type="button" className={`${WT_SECONDARY} mt-2`} onClick={() => setSpecFor(u)}>
                            <Pencil className="size-4" aria-hidden="true" />
                            Spec
                          </button>
                        )}
                      </>
                    );
                  }}
                />
              </WtPanel>
            </>
          )}
        </div>
      </section>

      {canWrite && (
        <SpecDialog unit={specFor} spec={specFor ? specOf.get(specFor.equipmentId) ?? null : null} catalog={catalog} pressureUnit={pressureUnit} onClose={() => setSpecFor(null)} />
      )}
      {pkg && canWrite && (
        <>
          <WtDialog
            open={!!removing}
            title="Remove from package"
            onClose={() => setRemoving(null)}
            footer={
              <>
                <button type="button" className={WT_SECONDARY} onClick={() => setRemoving(null)} disabled={removeUnit.isPending}>
                  Keep it
                </button>
                <button
                  type="button"
                  className={WT_PRIMARY}
                  disabled={removeUnit.isPending}
                  onClick={() =>
                    removing &&
                    removeUnit.mutate(removing.id, {
                      onSuccess: () => {
                        toast.success('Removed from the package');
                        setRemoving(null);
                      },
                      onError: (err) => setRemoveError(wtRefusal(err, 'The unit could not be removed.')),
                    })
                  }
                >
                  {removeUnit.isPending ? 'Removing…' : 'Remove'}
                </button>
              </>
            }
          >
            <WtFormError message={removeError} />
            <p className="text-sm text-fieldiq-text">Takes {removing?.equipmentName ?? 'this unit'} out of {pkg.name}. The unit and its spec stay in the register.</p>
          </WtDialog>
          <WtDialog
            open={deleting}
            title="Delete package"
            onClose={() => setDeleting(false)}
            footer={
              <>
                <button type="button" className={WT_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                  Keep package
                </button>
                <button
                  type="button"
                  className={WT_PRIMARY}
                  disabled={del.isPending}
                  onClick={() =>
                    del.mutate(pkg.id, {
                      onSuccess: () => {
                        toast.success('Package deleted');
                        navigate('/well-testing/packages', { replace: true });
                      },
                      onError: (err) => setDeleteError(wtRefusal(err, 'The package could not be deleted.')),
                    })
                  }
                >
                  {del.isPending ? 'Deleting…' : 'Delete package'}
                </button>
              </>
            }
          >
            <WtFormError message={deleteError} />
            <p className="text-sm text-fieldiq-text">Removes {pkg.name}. Its units stay in the equipment register. A package on an open job cannot be deleted.</p>
          </WtDialog>
        </>
      )}
    </div>
  );
}
